import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { examApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Plus, Pencil, Send, FileDown, Loader2, Copy, Lock, FileText, Shuffle,
} from 'lucide-react';

const STATUS_VARIANT = {
  draft: 'secondary',
  submitted: 'default',
  approved: 'default',
  rejected: 'destructive',
  locked: 'default',
};

const MyPapersPage = () => {
  const navigate = useNavigate();
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await examApi.listPapers();
      setPapers(data.papers || []);
    } catch (e) {
      toast.error('Failed to load papers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (id) => {
    if (!window.confirm('Submit this paper for approval? You will not be able to edit it after.')) return;
    try {
      await examApi.submitPaper(id);
      toast.success('Submitted for approval');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    }
  };

  const generateSets = async (id) => {
    const n = parseInt(window.prompt('How many additional sets? (1-4)', '2'), 10);
    if (!n || n < 1 || n > 4) return;
    try {
      const { data } = await examApi.generateSets(id, n);
      toast.success(`Generated ${data.count} additional set(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate sets');
    }
  };

  const downloadPdf = (id) => {
    window.open(examApi.pdfUrl(id), '_blank');
  };

  const tabsList = [
    { key: 'all', label: 'All' },
    { key: 'draft', label: 'Drafts' },
    { key: 'submitted', label: 'Submitted' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'locked', label: 'Locked' },
  ];

  const visible = tab === 'all' ? papers : papers.filter(p => p.status === tab);

  return (
    <div className="space-y-6" data-testid="my-papers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" /> Question Papers
          </h1>
          <p className="text-sm text-muted-foreground">Manage drafts, submit for approval, download secure PDFs</p>
        </div>
        <Button onClick={() => navigate('/examinations/create-paper')} data-testid="new-paper-btn">
          <Plus className="h-4 w-4 mr-2" /> New Paper
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {tabsList.map(t => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>
              {t.label}
              <span className="ml-2 text-xs text-muted-foreground">
                ({t.key === 'all' ? papers.length : papers.filter(p => p.status === t.key).length})
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={tab}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {loading ? 'Loading…' : `${visible.length} paper${visible.length === 1 ? '' : 's'}`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : visible.length === 0 ? (
                <p className="text-center py-10 text-muted-foreground">No papers in this view.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Paper Code · Title</TableHead>
                        <TableHead>Class · Subject</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Marks</TableHead>
                        <TableHead>Set</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visible.map(p => (
                        <TableRow key={p.id} data-testid={`paper-row-${p.id}`}>
                          <TableCell>
                            <p className="font-medium">{p.title}</p>
                            <p className="text-xs text-muted-foreground font-mono">{p.paper_code}</p>
                            <p className="text-xs text-muted-foreground">by {p.created_by_name}</p>
                          </TableCell>
                          <TableCell>
                            {p.class_name}<br />
                            <span className="text-xs text-muted-foreground">{p.subject_name}</span>
                          </TableCell>
                          <TableCell>{p.exam_type}</TableCell>
                          <TableCell>{p.total_marks}</TableCell>
                          <TableCell><Badge variant="outline">{p.set_label}</Badge></TableCell>
                          <TableCell>
                            <Badge variant={STATUS_VARIANT[p.status] || 'outline'}>
                              {p.status === 'locked' && <Lock className="h-3 w-3 mr-1" />}
                              {p.status?.toUpperCase()}
                            </Badge>
                            {p.status === 'rejected' && p.rejection_reason && (
                              <p className="text-xs text-red-500 mt-1 max-w-xs">
                                Reason: {p.rejection_reason}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            {p.status === 'draft' && (
                              <>
                                <Button size="sm" variant="outline"
                                        onClick={() => navigate(`/examinations/edit-paper/${p.id}`)}
                                        data-testid={`edit-paper-${p.id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" onClick={() => submit(p.id)}
                                        data-testid={`submit-paper-${p.id}`}>
                                  <Send className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                            {p.status === 'rejected' && (
                              <Button size="sm" variant="outline"
                                      onClick={() => navigate(`/examinations/edit-paper/${p.id}`)}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {(p.status === 'approved' || p.status === 'locked') && !p.parent_paper_id && (
                              <Button size="sm" variant="outline" title="Generate Set B/C/D"
                                      onClick={() => generateSets(p.id)}
                                      data-testid={`gen-sets-${p.id}`}>
                                <Shuffle className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => downloadPdf(p.id)}
                                    data-testid={`download-pdf-${p.id}`}>
                              <FileDown className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyPapersPage;
